import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigation } from 'react-router';
import { projectNavMode } from '@gonfalon/constants/constants';
import { enableStickyProjectNavigationMode } from '@gonfalon/dogfood-flags/dogfood-flags';
import { usePutProjectNavMode } from '@gonfalon/rest-api/hooks/usePutProjectNavMode';

import type { NavMode } from '../internal/Navigation/NavModeSwitcher';
import { useRouteNavMode } from '../internal/ProjectLayout/useRouteNavMode';

export interface ProjectNavModePreference {
  /**
   * What the project nav shows. A page that belongs to one nav decides it, and
   * once the member is in a mode they stay in it, including on the pages both
   * navs share. See `useProjectNavMode`.
   */
  navMode: NavMode;
  /**
   * The mode the member chose for themselves, which is what the next cold load
   * reads. Browsing never moves it, so a link someone sent them cannot change
   * where they land. See `ProjectIndexRedirect`.
   */
  preferredNavMode: NavMode;
  /**
   * The member picked this mode with the switcher, and it is stored. Browsing
   * into a section is not a choice, so nothing else moves the preference.
   */
  setNavMode: (mode: NavMode) => void;
}

const ProjectNavModePreferenceContext = createContext<ProjectNavModePreference | null>(null);

/**
 * Holds the project navigation mode for the lifetime of the page load.
 *
 * Mounted at the app shell because anything lower remounts and loses the carry
 * below. `appRoutes` mounts `ProjectLayout` twice as sibling routes, and account
 * settings sits outside the project subtree entirely.
 */
export function ProjectNavModePreferenceProvider({ children }: { children: ReactNode }) {
  // Read from the page config, which the server writes into the document on
  // every load, so this is current when the page opens and costs no request.
  // Nothing refreshes it afterwards on purpose: it feeds `navMode` on the pages
  // both navs share, so a toggle made in another tab would move the switcher
  // under the member.
  const [preferredNavMode, setPreferredNavMode] = useState<NavMode>(() => projectNavMode() ?? 'code');

  // Carried, not derived, and never persisted. Open a link to AI Configs and the
  // member is in agent mode from then on, so clicking through to a page both navs
  // share (Metrics, say) does not drop them back into code. Arriving somewhere is
  // not the same as choosing it, so the stored preference is untouched.
  const [carriedNavMode, setCarriedNavMode] = useState<NavMode | null>(null);
  const routeNavMode = useRouteNavMode();

  useEffect(() => {
    if (routeNavMode !== undefined) {
      setCarriedNavMode(routeNavMode);
    }
  }, [routeNavMode]);

  // The mode the member just picked, while the navigation it started is still in
  // flight. `routeNavMode` reads committed router state, so on a mode-specific
  // page it keeps answering with the page the member is leaving, and without this
  // the switcher would not move until the destination commits. That is around a
  // second on a cold chunk, on a `radiogroup` whose `aria-checked` has to follow
  // activation.
  const [pendingNavMode, setPendingNavMode] = useState<NavMode | null>(null);
  const navigation = useNavigation();

  // Released as soon as the route moves, during render rather than from an
  // effect. On the render that lands the new page the route has already changed
  // and an effect has not run yet, so an effect would leave the intent in front
  // of the new route for that one render and paint the wrong nav on the page just
  // arrived at.
  const [routeNavModeWhenPicked, setRouteNavModeWhenPicked] = useState(routeNavMode);
  if (routeNavMode !== routeNavModeWhenPicked) {
    setRouteNavModeWhenPicked(routeNavMode);
    setPendingNavMode(null);
  }

  // A navigation can also end without the route moving, and there is no render to
  // key off in that case. Nothing is painted wrong in the meantime, so an effect
  // is enough. Keyed on the state so it only runs on a change: the click and the
  // navigate it triggers land together, and a run at that moment would drop the
  // intent before the router had picked it up.
  useEffect(() => {
    if (navigation.state === 'idle') {
      setPendingNavMode(null);
    }
  }, [navigation.state]);

  const { mutate: putProjectNavMode } = usePutProjectNavMode();

  const setNavMode = useCallback(
    (mode: NavMode) => {
      setPendingNavMode(mode);
      setCarriedNavMode(mode);
      // Persistence is the flagged part. With the flag off the nav still moves;
      // the endpoint is hidden, so there is nobody to tell.
      //
      // Returning here is also what holds `preferredNavMode` at whatever the page
      // config said, since a write is the only thing that moves it. That value
      // does reach the nav on the pages both navs share, so the flag is not purely
      // about storage: `config_get.go` sends `code` while it is off, which is what
      // keeps that indirect path quiet.
      if (!enableStickyProjectNavigationMode()) {
        return;
      }

      // Optimistic, so `ProjectIndexRedirect` sends the member to the section
      // they just picked without waiting for the round trip. Rolled back on
      // failure: that redirect is decided from this value, so a client holding a
      // mode the server rejected would land them somewhere the next load does
      // not.
      const previous = preferredNavMode;
      setPreferredNavMode(mode);

      // Toggling again before this lands is safe: a second `mutate` on the same
      // hook replaces these callbacks, so the earlier write settles silently and
      // only the newest one answers. Nothing here has to track that.
      putProjectNavMode(
        { body: { projectNavMode: mode } },
        {
          onError: () => setPreferredNavMode(previous),
          // The stored record, which the server resolves. A mode it will not
          // honour comes back collapsed, and this is where the client finds out.
          onSuccess: (data) => setPreferredNavMode(data.projectNavMode),
        },
      );
    },
    [putProjectNavMode, preferredNavMode],
  );

  // A mode the member just picked outranks the page they are leaving. Below that
  // the route wins, so arriving somewhere never flashes the other nav first while
  // the carry effect catches up.
  const navMode = pendingNavMode ?? routeNavMode ?? carriedNavMode ?? preferredNavMode;

  const value = useMemo(() => ({ navMode, preferredNavMode, setNavMode }), [navMode, preferredNavMode, setNavMode]);

  return <ProjectNavModePreferenceContext.Provider value={value}>{children}</ProjectNavModePreferenceContext.Provider>;
}

/**
 * Throws rather than falling back to local state. A silent fallback would work
 * on first render and only go wrong after a remount, the one case this exists
 * for.
 */
export function useProjectNavModePreference(): ProjectNavModePreference {
  const preference = useContext(ProjectNavModePreferenceContext);
  if (preference === null) {
    throw new Error('useProjectNavMode must be rendered inside a ProjectNavModePreferenceProvider');
  }
  return preference;
}
